import React, { useEffect, useState } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { useProvider } from '../../contexts/ProviderContext';
import { useToast } from '../../contexts/ToastContext';
import { variantLabel, ProductPricePosition } from '../../lib/pricePosition';
import PricePositionBadge from './PricePositionBadge';

interface ProductEditModalProps {
  productId: string | null;
  providerId?: string | null;
  onClose: () => void;
  onSave: () => void;
  readOnly?: boolean;
  /** Supplier's own standing per size, as last saved; absent in the merchant view. */
  pricePosition?: ProductPricePosition;
}

interface Variant {
  variant_id: string | null;
  variant_sku: string | null;
  variant_name: string | null;
  variant_value: string | null;
  quantity: number;
  price: number;
  external_variant_id: string | null;
  inventory_id: string | null;
  isNew?: boolean;
}

interface ProductData {
  product_id: string;
  title: string;
  description: string;
  vendor: string;
  category: string;
  product_type: string;
  photo_url: string;
  tags: string[];
  collections: string[];
  product_sku: string;
  variants: Variant[];
}

const ProductEditModal: React.FC<ProductEditModalProps> = ({ productId, providerId: providerIdProp, onClose, onSave, readOnly = false, pricePosition }) => {
  const { currentProviderId: contextProviderId } = useProvider();
  const currentProviderId = providerIdProp || contextProviderId;
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'info' | 'variants'>('info');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [productData, setProductData] = useState<ProductData>({
    product_id: '',
    title: '',
    description: '',
    vendor: '',
    category: '',
    product_type: '',
    photo_url: '',
    tags: [],
    collections: [],
    product_sku: '',
    variants: [],
  });

  const [newTag, setNewTag] = useState('');
  const [newCollection, setNewCollection] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Load product data
  useEffect(() => {
    if (!productId) {
      setLoading(false);
      return;
    }

    const loadProduct = async () => {
      try {
        setLoading(true);

        // Fetch product with all variants from product_catalog_view
        const { data, error: fetchError } = await supabase
          .from('product_catalog_view')
          .select('*')
          .eq('product_id', productId)
          .eq('provider_id', currentProviderId);

        if (fetchError) throw fetchError;
        if (!data || data.length === 0) throw new Error('Product not found');

        // Parse the data into our structure
        const firstRow = data[0];
        const variants: Variant[] = [];
        const variantMap = new Map<string, Variant>();

        data.forEach((row: any) => {
          if (row.variant_id && !variantMap.has(row.variant_id)) {
            variantMap.set(row.variant_id, {
              variant_id: row.variant_id,
              variant_sku: row.variant_sku,
              variant_name: row.variant_name,
              variant_value: row.variant_value,
              quantity: row.quantity || 0,
              price: row.price || 0,
              external_variant_id: row.external_variant_id,
              inventory_id: row.inventory_id,
            });
          }
        });

        setProductData({
          product_id: firstRow.product_id,
          title: firstRow.title || '',
          description: firstRow.description || '',
          vendor: firstRow.vendor || '',
          category: firstRow.category || '',
          product_type: firstRow.product_type || '',
          photo_url: firstRow.photo_url || '',
          tags: firstRow.tags || [],
          collections: firstRow.collections || [],
          product_sku: firstRow.product_sku || '',
          variants: Array.from(variantMap.values()),
        });
      } catch (e: any) {
        console.error('Error loading product:', e);
        toast.error(e.message || 'Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    loadProduct();
  }, [productId, currentProviderId]);

  // Handle product field changes
  const updateField = (field: keyof ProductData, value: any) => {
    setProductData(prev => ({ ...prev, [field]: value }));
  };

  // Handle tag management
  const addTag = () => {
    if (newTag.trim() && !productData.tags.includes(newTag.trim())) {
      updateField('tags', [...productData.tags, newTag.trim()]);
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    updateField('tags', productData.tags.filter(t => t !== tag));
  };

  // Handle collection management
  const addCollection = () => {
    if (newCollection.trim() && !productData.collections.includes(newCollection.trim())) {
      updateField('collections', [...productData.collections, newCollection.trim()]);
      setNewCollection('');
    }
  };

  const removeCollection = (collection: string) => {
    updateField('collections', productData.collections.filter(c => c !== collection));
  };

  // Handle variant changes
  const updateVariant = (index: number, field: keyof Variant, value: any) => {
    const updated = [...productData.variants];
    updated[index] = { ...updated[index], [field]: value };
    updateField('variants', updated);
  };

  const addVariant = () => {
    updateField('variants', [
      ...productData.variants,
      {
        variant_id: null,
        variant_sku: '',
        variant_name: 'Size',
        variant_value: '',
        quantity: 0,
        price: 0,
        external_variant_id: null,
        inventory_id: null,
        isNew: true,
      },
    ]);
  };

  const deleteVariant = (index: number) => {
    const updated = productData.variants.filter((_, i) => i !== index);
    updateField('variants', updated);
  };

  // Save product
  const handleSave = async () => {
    try {
      setSaving(true);

      // Validation
      if (!productData.title.trim()) {
        throw new Error('Title is required');
      }
      if (productData.variants.length === 0) {
        throw new Error('At least one variant is required');
      }
      for (const variant of productData.variants) {
        if (!variant.variant_value || !variant.variant_value.trim()) {
          throw new Error('All variants must have a value');
        }
        if (variant.price < 0) {
          throw new Error('Price cannot be negative');
        }
        if (variant.quantity < 0) {
          throw new Error('Quantity cannot be negative');
        }
      }

      // Update product_descriptions
      const { error: productError } = await supabase
        .from('product_descriptions')
        .update({
          title: productData.title,
          description: productData.description,
          vendor: productData.vendor,
          category: productData.category,
          product_type: productData.product_type,
          photo_url: productData.photo_url,
          tags: productData.tags,
          collections: productData.collections,
          updated_at: new Date().toISOString(),
        })
        .eq('id', productData.product_id);

      if (productError) throw productError;

      // Update or create variants
      for (const variant of productData.variants) {
        if (variant.isNew || !variant.variant_id) {
          // Create new variant
          const { data: newVariant, error: variantError } = await supabase
            .from('product_variants')
            .insert({
              product_description_id: productData.product_id,
              provider_id: currentProviderId,
              sku: variant.variant_sku || productData.product_sku,
              variant_name: variant.variant_name,
              variant_value: variant.variant_value,
            })
            .select()
            .single();

          if (variantError) throw variantError;

          // Create inventory for new variant
          const { error: invError } = await supabase
            .from('product_inventory')
            .insert({
              product_variant_id: newVariant.id,
              provider_id: currentProviderId,
              quantity: variant.quantity,
              price: variant.price,
            });

          if (invError) throw invError;
        } else {
          // Update existing variant
          const { error: variantError } = await supabase
            .from('product_variants')
            .update({
              variant_name: variant.variant_name,
              variant_value: variant.variant_value,
              sku: variant.variant_sku,
              updated_at: new Date().toISOString(),
            })
            .eq('id', variant.variant_id);

          if (variantError) throw variantError;

          // Update inventory
          if (variant.inventory_id) {
            const { error: invError } = await supabase
              .from('product_inventory')
              .update({
                quantity: variant.quantity,
                price: variant.price,
                updated_at: new Date().toISOString(),
              })
              .eq('id', variant.inventory_id);

            if (invError) throw invError;
          } else {
            // Create inventory if it doesn't exist
            const { error: invError } = await supabase
              .from('product_inventory')
              .insert({
                product_variant_id: variant.variant_id,
                provider_id: currentProviderId,
                quantity: variant.quantity,
                price: variant.price,
              });

            if (invError) throw invError;
          }
        }
      }

      // Success
      onSave();
      onClose();
    } catch (e: any) {
      console.error('Error saving product:', e);
      toast.error(e.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  // Delete product
  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      setSaving(true);

      // Delete product (cascades to variants and inventory)
      const { error: deleteError } = await supabase
        .from('product_descriptions')
        .delete()
        .eq('id', productData.product_id);

      if (deleteError) throw deleteError;

      onSave();
      onClose();
    } catch (e: any) {
      console.error('Error deleting product:', e);
      toast.error(e.message || 'Failed to delete product');
      setDeleteConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  if (!productId) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="modal-body rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/[0.08]">
          <div>
            <h2 className="text-xl font-bold text-white ">
              {readOnly ? 'View Product' : 'Edit Product'}
            </h2>
            {readOnly && (
              <p className="text-sm text-blue-600  mt-1">Read-Only Mode</p>
            )}
          </div>
          <button onClick={onClose} className="text-white/25 hover:text-white/50">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/[0.08] px-6">
          <button
            onClick={() => setActiveTab('info')}
            className={`px-4 py-3  text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'info'
                ? 'border-primary-600 text-orange-400'
                : 'border-transparent text-white/35 hover:text-white/70'
            }`}
          >
            Product Info
          </button>
          <button
            onClick={() => setActiveTab('variants')}
            className={`px-4 py-3  text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'variants'
                ? 'border-primary-600 text-orange-400'
                : 'border-transparent text-white/35 hover:text-white/70'
            }`}
          >
            Variants & Inventory
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}

          {!loading && activeTab === 'info' && (
            <div className="space-y-6">
              {/* Image Preview */}
              {productData.photo_url && (
                <div className="flex justify-center">
                  <img
                    src={productData.photo_url}
                    alt={productData.title}
                    className="w-48 h-48 object-cover rounded border border-white/15"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/192x192?text=No+Image';
                    }}
                  />
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Title *
                </label>
                <input
                  type="text"
                  value={productData.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  disabled={readOnly}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Description
                </label>
                <textarea
                  value={productData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  disabled={readOnly}
                  rows={4}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                />
              </div>

              {/* Vendor & Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white/70  mb-1">
                    Vendor
                  </label>
                  <input
                    type="text"
                    value={productData.vendor}
                    onChange={(e) => updateField('vendor', e.target.value)}
                    disabled={readOnly}
                    className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/70  mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={productData.category}
                    onChange={(e) => updateField('category', e.target.value)}
                    disabled={readOnly}
                    className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Product Type */}
              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Product Type
                </label>
                <input
                  type="text"
                  value={productData.product_type}
                  onChange={(e) => updateField('product_type', e.target.value)}
                  disabled={readOnly}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                />
              </div>

              {/* Photo URL */}
              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Photo URL
                </label>
                <input
                  type="text"
                  value={productData.photo_url}
                  onChange={(e) => updateField('photo_url', e.target.value)}
                  disabled={readOnly}
                  className="w-full px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Tags
                </label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {productData.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-orange-500/15 text-orange-500 rounded  text-sm"
                    >
                      {tag}
                      {!readOnly && (
                        <button onClick={() => removeTag(tag)} className="text-primary-900 hover:text-orange-400">
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {!readOnly && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                      placeholder="Add tag..."
                      className="flex-1 px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                    />
                    <button
                      onClick={addTag}
                      className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm hover:bg-orange-400 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>

              {/* Collections */}
              <div>
                <label className="block text-sm font-medium text-white/70  mb-1">
                  Collections
                </label>
                <div className="flex gap-2 mb-2 flex-wrap">
                  {productData.collections.map((collection) => (
                    <span
                      key={collection}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-transparent text-white/70 rounded  text-sm"
                    >
                      {collection}
                      {!readOnly && (
                        <button onClick={() => removeCollection(collection)} className="text-white hover:text-white/50">
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
                {!readOnly && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newCollection}
                      onChange={(e) => setNewCollection(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCollection())}
                      placeholder="Add collection..."
                      className="flex-1 px-3 py-2 border border-white/15 rounded focus:outline-none focus:ring-2 focus:ring-primary-500  text-sm"
                    />
                    <button
                      onClick={addCollection}
                      className="px-4 py-2 bg-gray-600 text-white rounded  text-sm hover:bg-gray-700"
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {!loading && activeTab === 'variants' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-white ">Variants</h3>
                {!readOnly && (
                  <button
                    onClick={addVariant}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-xl text-sm hover:bg-orange-400 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Variant
                  </button>
                )}
              </div>

              {productData.variants.length === 0 && (
                <div className="text-center py-8 text-white/35  text-sm">
                  No variants yet. Click "Add Variant" to create one.
                </div>
              )}

              <div className="space-y-3">
                {productData.variants.map((variant, index) => (
                  <div key={index} className="border border-white/15 rounded p-4 bg-transparent">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-white/50  mb-1">Name</label>
                        <input
                          type="text"
                          value={variant.variant_name || ''}
                          onChange={(e) => updateVariant(index, 'variant_name', e.target.value)}
                          disabled={readOnly}
                          className="w-full px-2 py-1 border border-white/15 rounded  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/50  mb-1">Value *</label>
                        <input
                          type="text"
                          value={variant.variant_value || ''}
                          onChange={(e) => updateVariant(index, 'variant_value', e.target.value)}
                          disabled={readOnly}
                          className="w-full px-2 py-1 border border-white/15 rounded  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/50  mb-1">Stock</label>
                        <input
                          type="number"
                          value={variant.quantity}
                          onChange={(e) => updateVariant(index, 'quantity', parseInt(e.target.value) || 0)}
                          disabled={readOnly}
                          min="0"
                          className="w-full px-2 py-1 border border-white/15 rounded  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-white/50  mb-1">Price (€)</label>
                        <input
                          type="number"
                          value={variant.price}
                          onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value) || 0)}
                          disabled={readOnly}
                          min="0"
                          step="0.01"
                          className="w-full px-2 py-1 border border-white/15 rounded  text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                        />
                      </div>
                    </div>
                    {variant.variant_id && pricePosition?.byVariant[variant.variant_id] && (
                      <div className="mt-3">
                        <PricePositionBadge label={variantLabel(pricePosition.byVariant[variant.variant_id])} />
                      </div>
                    )}
                    {!readOnly && (
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => deleteVariant(index)}
                          className="inline-flex items-center gap-1 px-3 py-1 text-red-400 hover:bg-red-500/10 rounded  text-sm"
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-white/[0.08]">
          {!readOnly ? (
            <>
              <button
                onClick={handleDelete}
                disabled={saving}
                className={`px-4 py-2 border-2 rounded  text-sm ${
                  deleteConfirm
                    ? 'border-red-600 bg-red-600 text-white hover:bg-red-700'
                    : 'border-red-600 text-red-400 hover:bg-red-500/10'
                } disabled:opacity-50`}
              >
                {deleteConfirm ? 'Click Again to Confirm Delete' : 'Delete Product'}
              </button>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-4 py-2 border border-white/15 text-white/70 rounded  text-sm hover:bg-white/[0.08] disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm hover:bg-orange-400 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </>
          ) : (
            <div className="w-full flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm hover:bg-orange-400 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductEditModal;
